import { config, locales, util, type core } from "zod"
import { useTranslations } from "next-intl"
import { PropsWithChildren, useEffect } from "react"

const jsonStringifyReplacer = (_: string, value: unknown): unknown => {
  if (typeof value === "bigint") {
    return value.toString()
  }
  return value
}

function joinValues<T extends unknown[]>(array: T, separator = " | "): string {
  return array
    .map((val) => (typeof val === "string" ? `'${val}'` : val))
    .join(separator)
}

const isRecord = (value: unknown): value is Record<string, unknown> => {
  if (typeof value !== "object" || value === null) return false

  for (const key in value) {
    if (!Object.prototype.hasOwnProperty.call(value, key)) return false
  }

  return true
}

const getKeyAndValues = (
  param: unknown,
  defaultKey: string
): {
  values: Record<string, unknown>
  key: string
} => {
  if (typeof param === "string") return { key: param, values: {} }

  if (isRecord(param)) {
    const key =
      "key" in param && typeof param.key === "string" ? param.key : defaultKey
    const values =
      "values" in param && isRecord(param.values) ? param.values : {}
    return { key, values }
  }

  return { key: defaultKey, values: {} }
}

const defaultLocaleError = locales.en().localeError!

function unwrapMessage(message: string | { message: string } | undefined | null): string {
  return typeof message === "string" ? message : message?.message ?? "Invalid input"
}

export function ZodTranslationProvider({ children }: PropsWithChildren) {
  const t = useTranslations("Zod")

  useEffect(() => {
    const previousCustomError = config().customError

    const customError: core.$ZodErrorMap = (issue) => {
      const defaultMessage = unwrapMessage(defaultLocaleError(issue))

      function transFn(
        key: string,
        options: { defaultValue: string } & Record<string, unknown>
      ): string {
        if (t.has(key))
          return String(t(key, options as Parameters<typeof t>[1]))
        return options.defaultValue
      }

      switch (issue.code) {
        case "invalid_type": {
          if (issue.input === undefined) {
            return transFn("errors.invalid_type_received_undefined", {
              defaultValue: defaultMessage,
            })
          }
          if (issue.input === null) {
            return transFn("errors.invalid_type_received_null", {
              defaultValue: defaultMessage,
            })
          }
          const received = util.parsedType(issue.input)
          return transFn("errors.invalid_type", {
            expected: transFn(`types.${issue.expected}`, {
              defaultValue: issue.expected,
            }),
            received: transFn(`types.${received}`, {
              defaultValue: received,
            }),
            defaultValue: defaultMessage,
          })
        }
        case "invalid_value": {
          if (issue.values.length === 1) {
            return transFn("errors.invalid_literal", {
              expected: JSON.stringify(issue.values[0], jsonStringifyReplacer),
              defaultValue: defaultMessage,
            })
          }
          return transFn("errors.invalid_enum_value", {
            options: joinValues(issue.values, ", "),
            received: String(issue.input),
            defaultValue: defaultMessage,
          })
        }
        case "unrecognized_keys":
          return transFn("errors.unrecognized_keys", {
            keys: joinValues(issue.keys, ", "),
            count: issue.keys.length,
            defaultValue: defaultMessage,
          })
        case "invalid_union": {
          const options = "options" in issue ? issue.options : undefined
          if (Array.isArray(options) && options.length > 0) {
            return transFn("errors.invalid_union_discriminator", {
              options: joinValues(options),
              defaultValue: defaultMessage,
            })
          }
          return transFn("errors.invalid_union", {
            defaultValue: defaultMessage,
          })
        }
        case "invalid_format": {
          if (issue.format === "starts_with") {
            return transFn("errors.invalid_string.startsWith", {
              startsWith: issue.prefix,
              defaultValue: defaultMessage,
            })
          }
          if (issue.format === "ends_with") {
            return transFn("errors.invalid_string.endsWith", {
              endsWith: issue.suffix,
              defaultValue: defaultMessage,
            })
          }
          return transFn(`errors.invalid_string.${issue.format}`, {
            validation: transFn(`validations.${issue.format}`, {
              defaultValue: issue.format,
            }),
            defaultValue: defaultMessage,
          })
        }
        case "too_small": {
          const minimum =
            issue.origin === "date"
              ? new Date(issue.minimum as number)
              : typeof issue.minimum === "bigint"
                ? Number(issue.minimum)
                : issue.minimum
          return transFn(
            `errors.too_small.${issue.origin}.${issue.exact
              ? "exact"
              : issue.inclusive
                ? "inclusive"
                : "not_inclusive"
            }`,
            {
              minimum,
              count: typeof minimum === "number" ? minimum : undefined,
              defaultValue: defaultMessage,
            }
          )
        }
        case "too_big": {
          const maximum =
            issue.origin === "date"
              ? new Date(issue.maximum as number)
              : typeof issue.maximum === "bigint"
                ? Number(issue.maximum)
                : issue.maximum
          return transFn(
            `errors.too_big.${issue.origin}.${issue.exact
              ? "exact"
              : issue.inclusive
                ? "inclusive"
                : "not_inclusive"
            }`,
            {
              maximum,
              count: typeof maximum === "number" ? maximum : undefined,
              defaultValue: defaultMessage,
            }
          )
        }
        case "custom": {
          const { key, values } = getKeyAndValues(
            issue.params?.i18n,
            "errors.custom"
          )

          return transFn(key, {
            ...values,
            defaultValue: defaultMessage,
          })
        }
        case "not_multiple_of":
          return transFn("errors.not_multiple_of", {
            multipleOf: issue.divisor,
            defaultValue: defaultMessage,
          })
        default:
          return defaultMessage
      }
    }

    config({ customError })

    return () => {
      config({ customError: previousCustomError })
    }
  }, [t])

  return children
}
